import {EnumValue} from '@/components/shared/EnumValue'
import {BadgeVariant} from '@/components/ui/badge'
import type {useI18n} from '@/i18n'
import {AppInstant} from '@/lib/date-utils'
import type {Instant, UUID} from './common'

type I18n = ReturnType<typeof useI18n>

/**
 * DTOs for service-account + API-key management (epic 626, sub-issue 626.06).
 *
 * `ApiKeyMint` carries the raw `key` field — that DTO is the ONLY place the
 * full `iam_<40>` token ever crosses the wire. Every other interface here
 * carries only `keyPrefix` (12 chars, non-secret).
 */

export interface ServiceAccount {
  id: UUID
  name: string
  description: string | null
  active: boolean
  createdAt: Instant
  createdBy: UUID | null
  activeKeyCount: number
}

export interface ServiceAccountCreateRequest {
  name: string
  description?: string | null
}

export interface ServiceAccountUpdateRequest {
  name: string
  description?: string | null
  active?: boolean | null
}

export interface ApiKey {
  id: UUID
  name: string
  keyPrefix: string
  active: boolean
  createdAt: Instant
  expiresAt: Instant | null
  lastUsedAt: Instant | null
  lastUsedIp: string | null
  revokedAt: Instant | null
  rateLimitPerMinute: number | null
}

export interface ApiKeyMint {
  id: UUID
  name: string
  key: string
  keyPrefix: string
  expiresAt: Instant | null
  createdAt: Instant
  rateLimitPerMinute: number | null
}

export interface ApiKeyCreateRequest {
  name: string
  expiresAt?: Instant | null
  rateLimitPerMinute?: number | null
}

/**
 * API-key status code derived from `(active, expiresAt, revokedAt)`.
 * Priority order per spec §8: REVOKED > INACTIVE > EXPIRED > ACTIVE.
 */
/* eslint-disable i18next/no-literal-string -- enum string codes mirror backend status values */
export const ApiKeyBadgeStatus = {
  REVOKED: 'REVOKED',
  INACTIVE: 'INACTIVE',
  EXPIRED: 'EXPIRED',
  ACTIVE: 'ACTIVE',
} as const
/* eslint-enable i18next/no-literal-string */
export type ApiKeyBadgeStatus = typeof ApiKeyBadgeStatus[keyof typeof ApiKeyBadgeStatus]

/**
 * Rich enum carrying badge variant + i18n label per status. Use
 * `ApiKeyBadge.fromKey(key, now)` to derive the correct status from a key.
 */
export class ApiKeyBadge extends EnumValue {
  static readonly REVOKED = new ApiKeyBadge(
    ApiKeyBadgeStatus.REVOKED,
    BadgeVariant.DESTRUCTIVE,
    (i18n: I18n) => i18n.apiKeys.status.revoked as string,
  )
  static readonly INACTIVE = new ApiKeyBadge(
    ApiKeyBadgeStatus.INACTIVE,
    BadgeVariant.SECONDARY,
    (i18n: I18n) => i18n.apiKeys.status.inactive as string,
  )
  static readonly EXPIRED = new ApiKeyBadge(
    ApiKeyBadgeStatus.EXPIRED,
    BadgeVariant.WARNING,
    (i18n: I18n) => i18n.apiKeys.status.expired as string,
  )
  static readonly ACTIVE = new ApiKeyBadge(
    ApiKeyBadgeStatus.ACTIVE,
    BadgeVariant.SUCCESS,
    (i18n: I18n) => i18n.apiKeys.status.active as string,
  )

  static readonly values: ReadonlyArray<ApiKeyBadge> = [
    ApiKeyBadge.REVOKED,
    ApiKeyBadge.INACTIVE,
    ApiKeyBadge.EXPIRED,
    ApiKeyBadge.ACTIVE,
  ]

  static fromKey(key: ApiKey, now: Instant = AppInstant.now()): ApiKeyBadge {
    if (key.revokedAt) return ApiKeyBadge.REVOKED
    if (!key.active) return ApiKeyBadge.INACTIVE
    if (key.expiresAt && key.expiresAt < now) return ApiKeyBadge.EXPIRED
    return ApiKeyBadge.ACTIVE
  }

  static of(code: ApiKeyBadgeStatus): ApiKeyBadge | undefined {
    return ApiKeyBadge.values.find(v => v.code === code)
  }
}
